import { defineCustomElements } from '@didomi/ui-atoms/loader';
import { __rest } from 'tslib';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Field } from 'formik';

const dashToPascalCase = (str) => str
    .toLowerCase()
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
const camelToDashCase = (str) => str.replace(/([A-Z])/g, (m) => `-${m[0].toLowerCase()}`);

const attachProps = (node, newProps, oldProps = {}) => {
    // some test frameworks don't render DOM elements, so we test here to make sure we are dealing with DOM first
    if (node instanceof Element) {
        // add any classes in className to the class list
        const className = getClassName(node.classList, newProps, oldProps);
        if (className !== '') {
            node.className = className;
        }
        Object.keys(newProps).forEach((name) => {
            if (name === 'children' ||
                name === 'style' ||
                name === 'ref' ||
                name === 'class' ||
                name === 'className' ||
                name === 'forwardedRef') {
                return;
            }
            if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                const eventName = name.substring(2);
                const eventNameLc = eventName[0].toLowerCase() + eventName.substring(1);
                if (typeof document !== 'undefined' && !isCoveredByReact(eventNameLc, document)) {
                    syncEvent(node, eventNameLc, newProps[name]);
                }
            }
            else {
                node[name] = newProps[name];
                const propType = typeof newProps[name];
                if (propType === 'string') {
                    node.setAttribute(camelToDashCase(name), newProps[name]);
                }
                else {
                    node[name] = newProps[name];
                }
            }
        });
    }
};
const getClassName = (classList, newProps, oldProps) => {
    const newClassProp = newProps.className || newProps.class;
    const oldClassProp = oldProps.className || oldProps.class;
    // map the classes to Maps for performance
    const currentClasses = arrayToMap(classList);
    const incomingPropClasses = arrayToMap(newClassProp ? newClassProp.split(' ') : []);
    const oldPropClasses = arrayToMap(oldClassProp ? oldClassProp.split(' ') : []);
    const finalClassNames = [];
    // loop through each of the current classes on the component
    // to see if it should be a part of the classNames added
    currentClasses.forEach((currentClass) => {
        if (incomingPropClasses.has(currentClass)) {
            // add it as its already included in classnames coming in from newProps
            finalClassNames.push(currentClass);
            incomingPropClasses.delete(currentClass);
        }
        else if (!oldPropClasses.has(currentClass)) {
            // add it as it has NOT been removed by user
            finalClassNames.push(currentClass);
        }
    });
    incomingPropClasses.forEach((s) => finalClassNames.push(s));
    return finalClassNames.join(' ');
};
/**
 * Checks if an event is supported in the current execution environment.
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
const isCoveredByReact = (eventNameSuffix, doc) => {
    const eventName = 'on' + eventNameSuffix;
    let isSupported = eventName in doc;
    if (!isSupported) {
        const element = doc.createElement('div');
        element.setAttribute(eventName, 'return;');
        isSupported = typeof element[eventName] === 'function';
    }
    return isSupported;
};
const syncEvent = (node, eventName, newEventHandler) => {
    const eventStore = node.__events || (node.__events = {});
    const oldEventHandler = eventStore[eventName];
    // Remove old listener so they don't double up.
    if (oldEventHandler) {
        node.removeEventListener(eventName, oldEventHandler);
    }
    // Bind new listener.
    node.addEventListener(eventName, (eventStore[eventName] = function handler(e) {
        if (newEventHandler) {
            newEventHandler.call(this, e);
        }
    }));
};
const arrayToMap = (arr) => {
    const map = new Map();
    arr.forEach((s) => map.set(s, s));
    return map;
};

// The comma in the type is to trick typescript because it things a single generic in a tsx file is jsx
const mergeRefs = (...refs) => (value) => refs.forEach((ref) => {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref != null) {
        // This is typed as readonly so we need to allow for override
        ref.current = value;
    }
});
const createForwardRef = (ReactComponent, displayName) => {
    const forwardRef = (props, ref) => {
        return React.createElement(ReactComponent, Object.assign({}, props, { forwardedRef: ref }));
    };
    forwardRef.displayName = displayName;
    return React.forwardRef(forwardRef);
};

const createReactComponent = (tagName, ReactComponentContext, manipulatePropsFunction) => {
    const displayName = dashToPascalCase(tagName);
    const ReactComponent = class extends React.Component {
        constructor(props) {
            super(props);
            this.setComponentElRef = (element) => {
                this.componentEl = element;
            };
        }
        componentDidMount() {
            this.componentDidUpdate(this.props);
        }
        componentDidUpdate(prevProps) {
            attachProps(this.componentEl, this.props, prevProps);
        }
        render() {
            const _a = this.props, { children, forwardedRef, style, className, ref } = _a, cProps = __rest(_a, ["children", "forwardedRef", "style", "className", "ref"]);
            let propsToPass = Object.keys(cProps).reduce((acc, name) => {
                if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                    const eventName = name.substring(2).toLowerCase();
                    if (typeof document !== 'undefined' && isCoveredByReact(eventName, document)) {
                        acc[name] = cProps[name];
                    }
                }
                else {
                    acc[name] = cProps[name];
                }
                return acc;
            }, {});
            if (manipulatePropsFunction) {
                propsToPass = manipulatePropsFunction(this.props, propsToPass);
            }
            let newProps = Object.assign(Object.assign({}, propsToPass), { ref: mergeRefs(forwardedRef, this.setComponentElRef), style });
            return React.createElement(tagName, newProps, children);
        }
        static get displayName() {
            return displayName;
        }
    };
    // If context was passed to createReactComponent then conditionally add it to the Component Class
    if (ReactComponentContext) {
        ReactComponent.contextType = ReactComponentContext;
    }
    return createForwardRef(ReactComponent, displayName);
};

/* eslint-disable */
let DidomiAutocomplete;
let DidomiAutocompleteInput;
let DidomiAutocompleteWithSearch;
let DidomiBackButtonLink;
let DidomiBackToTopButton;
let DidomiBaseInput;
let DidomiBottomBar;
let DidomiButton;
let DidomiButtonGroup;
let DidomiCard;
let DidomiCardContainer;
let DidomiCardRadio;
let DidomiCarousel;
let DidomiCarouselContentSlide;
let DidomiCarouselSlide;
let DidomiCheckbox;
let DidomiChip;
let DidomiChipList;
let DidomiCollapse;
let DidomiCollapsibleCard;
let DidomiCollapsibleSection;
let DidomiCollapsibleTabHeader;
let DidomiCollapsibleTabHeaders;
let DidomiCollapsibleTabHeading;
let DidomiCollapsibleTabPanel;
let DidomiCollapsibleTabPanels;
let DidomiCollapsibleTabs;
let DidomiColorItem;
let DidomiColorPicker;
let DidomiColorPickerInput;
let DidomiContentAnalyticsCard;
let DidomiContentPromotionCard;
let DidomiContentPromotionCardShortenedItems;
let DidomiControlFlowHeader;
let DidomiCopyText;
let DidomiCtaPopIn;
let DidomiDatepicker;
let DidomiDrawer;
let DidomiDrawerContent;
let DidomiDrawerHeader;
let DidomiDropdown;
let DidomiEditableTextBox;
let DidomiEmptyState;
let DidomiErrorState;
let DidomiFabButton;
let DidomiFeatureHeader;
let DidomiFiltersBar;
let DidomiFiltersContainer;
let DidomiFloatingPasswordMeter;
let DidomiHeaderIllustration;
let DidomiHeaderInput;
let DidomiHintbox;
let DidomiHubspotForm;
let DidomiIcon;
let DidomiIconButton;
let DidomiIllustration;
let DidomiInstructionStep;
let DidomiLanguageFlag;
let DidomiLargePush;
let DidomiLink;
let DidomiListShortener;
let DidomiLoader;
let DidomiMenu;
let DidomiMenuItem;
let DidomiMenuSeparator;
let DidomiModal;
let DidomiModalActions;
let DidomiModalContent;
let DidomiModalHeader;
let DidomiNavTabContent;
let DidomiNavTabContents;
let DidomiNavTabHeader;
let DidomiNavTabHeaders;
let DidomiNavTabHeading;
let DidomiNavTabs;
let DidomiNumberInput;
let DidomiOption;
let DidomiPaginator;
let DidomiPartnerButton;
let DidomiPasswordInput;
let DidomiPasswordMeter;
let DidomiProgressBar;
let DidomiPromotionalPush;
let DidomiRadio;
let DidomiRadioGroup;
let DidomiRightPanel;
let DidomiRightPanelTab;
let DidomiSearchBar;
let DidomiSecondaryHeader;
let DidomiSelect;
let DidomiSelectCounter;
let DidomiSelectOption;
let DidomiSelectOptions;
let DidomiSelectPlainInput;
let DidomiSelectionBlock;
let DidomiSelectionCardItem;
let DidomiSkeleton;
let DidomiSmallPush;
let DidomiSmallPushCompact;
let DidomiSnackbar;
let DidomiStepper;
let DidomiStepperStep;
let DidomiSwitch;
let DidomiTabHeader;
let DidomiTabHeaders;
let DidomiTabHeading;
let DidomiTabPanel;
let DidomiTabPanels;
let DidomiTable;
let DidomiTableBody;
let DidomiTableCard;
let DidomiTableCardItem;
let DidomiTableHeaderRow;
let DidomiTableHeading;
let DidomiTableRow;
let DidomiTableTd;
let DidomiTableTh;
let DidomiTabs;
let DidomiTextInput;
let DidomiTextSearchInput;
let DidomiTextarea;
let DidomiThemingCardOption;
let DidomiTimeline;
let DidomiTimelineItem;
let DidomiToggleButton;
let DidomiToggleButtonOption;
let DidomiTooltip;
let DidomiTreeCard;
let DidomiTreeCardBranch;
let DidomiTreeCardBranchItem;
let DidomiUrlInput;
const setupComponents = (prefix) => {
    DidomiAutocomplete = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-autocomplete`);
    DidomiAutocompleteInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-autocomplete-input`);
    DidomiAutocompleteWithSearch = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-autocomplete-with-search`);
    DidomiBackButtonLink = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-back-button-link`);
    DidomiBackToTopButton = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-back-to-top-button`);
    DidomiBaseInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-base-input`);
    DidomiBottomBar = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-bottom-bar`);
    DidomiButton = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-button`);
    DidomiButtonGroup = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-button-group`);
    DidomiCard = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-card`);
    DidomiCardContainer = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-card-container`);
    DidomiCardRadio = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-card-radio`);
    DidomiCarousel = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-carousel`);
    DidomiCarouselContentSlide = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-carousel-content-slide`);
    DidomiCarouselSlide = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-carousel-slide`);
    DidomiCheckbox = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-checkbox`);
    DidomiChip = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-chip`);
    DidomiChipList = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-chip-list`);
    DidomiCollapse = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapse`);
    DidomiCollapsibleCard = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-card`);
    DidomiCollapsibleSection = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-section`);
    DidomiCollapsibleTabHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-tab-header`);
    DidomiCollapsibleTabHeaders = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-tab-headers`);
    DidomiCollapsibleTabHeading = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-tab-heading`);
    DidomiCollapsibleTabPanel = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-tab-panel`);
    DidomiCollapsibleTabPanels = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-tab-panels`);
    DidomiCollapsibleTabs = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-collapsible-tabs`);
    DidomiColorItem = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-color-item`);
    DidomiColorPicker = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-color-picker`);
    DidomiColorPickerInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-color-picker-input`);
    DidomiContentAnalyticsCard = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-content-analytics-card`);
    DidomiContentPromotionCard = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-content-promotion-card`);
    DidomiContentPromotionCardShortenedItems = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-content-promotion-card-shortened-items`);
    DidomiControlFlowHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-control-flow-header`);
    DidomiCopyText = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-copy-text`);
    DidomiCtaPopIn = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-cta-pop-in`);
    DidomiDatepicker = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-datepicker`);
    DidomiDrawer = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-drawer`);
    DidomiDrawerContent = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-drawer-content`);
    DidomiDrawerHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-drawer-header`);
    DidomiDropdown = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-dropdown`);
    DidomiEditableTextBox = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-editable-text-box`);
    DidomiEmptyState = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-empty-state`);
    DidomiErrorState = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-error-state`);
    DidomiFabButton = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-fab-button`);
    DidomiFeatureHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-feature-header`);
    DidomiFiltersBar = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-filters-bar`);
    DidomiFiltersContainer = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-filters-container`);
    DidomiFloatingPasswordMeter = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-floating-password-meter`);
    DidomiHeaderIllustration = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-header-illustration`);
    DidomiHeaderInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-header-input`);
    DidomiHintbox = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-hintbox`);
    DidomiHubspotForm = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-hubspot-form`);
    DidomiIcon = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-icon`);
    DidomiIconButton = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-icon-button`);
    DidomiIllustration = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-illustration`);
    DidomiInstructionStep = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-instruction-step`);
    DidomiLanguageFlag = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-language-flag`);
    DidomiLargePush = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-large-push`);
    DidomiLink = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-link`);
    DidomiListShortener = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-list-shortener`);
    DidomiLoader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-loader`);
    DidomiMenu = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-menu`);
    DidomiMenuItem = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-menu-item`);
    DidomiMenuSeparator = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-menu-separator`);
    DidomiModal = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-modal`);
    DidomiModalActions = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-modal-actions`);
    DidomiModalContent = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-modal-content`);
    DidomiModalHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-modal-header`);
    DidomiNavTabContent = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-nav-tab-content`);
    DidomiNavTabContents = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-nav-tab-contents`);
    DidomiNavTabHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-nav-tab-header`);
    DidomiNavTabHeaders = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-nav-tab-headers`);
    DidomiNavTabHeading = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-nav-tab-heading`);
    DidomiNavTabs = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-nav-tabs`);
    DidomiNumberInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-number-input`);
    DidomiOption = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-option`);
    DidomiPaginator = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-paginator`);
    DidomiPartnerButton = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-partner-button`);
    DidomiPasswordInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-password-input`);
    DidomiPasswordMeter = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-password-meter`);
    DidomiProgressBar = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-progress-bar`);
    DidomiPromotionalPush = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-promotional-push`);
    DidomiRadio = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-radio`);
    DidomiRadioGroup = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-radio-group`);
    DidomiRightPanel = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-right-panel`);
    DidomiRightPanelTab = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-right-panel-tab`);
    DidomiSearchBar = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-search-bar`);
    DidomiSecondaryHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-secondary-header`);
    DidomiSelect = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-select`);
    DidomiSelectCounter = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-select-counter`);
    DidomiSelectOption = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-select-option`);
    DidomiSelectOptions = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-select-options`);
    DidomiSelectPlainInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-select-plain-input`);
    DidomiSelectionBlock = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-selection-block`);
    DidomiSelectionCardItem = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-selection-card-item`);
    DidomiSkeleton = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-skeleton`);
    DidomiSmallPush = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-small-push`);
    DidomiSmallPushCompact = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-small-push-compact`);
    DidomiSnackbar = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-snackbar`);
    DidomiStepper = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-stepper`);
    DidomiStepperStep = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-stepper-step`);
    DidomiSwitch = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-switch`);
    DidomiTabHeader = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tab-header`);
    DidomiTabHeaders = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tab-headers`);
    DidomiTabHeading = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tab-heading`);
    DidomiTabPanel = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tab-panel`);
    DidomiTabPanels = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tab-panels`);
    DidomiTable = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table`);
    DidomiTableBody = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-body`);
    DidomiTableCard = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-card`);
    DidomiTableCardItem = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-card-item`);
    DidomiTableHeaderRow = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-header-row`);
    DidomiTableHeading = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-heading`);
    DidomiTableRow = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-row`);
    DidomiTableTd = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-td`);
    DidomiTableTh = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-table-th`);
    DidomiTabs = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tabs`);
    DidomiTextInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-text-input`);
    DidomiTextSearchInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-text-search-input`);
    DidomiTextarea = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-textarea`);
    DidomiThemingCardOption = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-theming-card-option`);
    DidomiTimeline = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-timeline`);
    DidomiTimelineItem = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-timeline-item`);
    DidomiToggleButton = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-toggle-button`);
    DidomiToggleButtonOption = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-toggle-button-option`);
    DidomiTooltip = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tooltip`);
    DidomiTreeCard = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tree-card`);
    DidomiTreeCardBranch = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tree-card-branch`);
    DidomiTreeCardBranchItem = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-tree-card-branch-item`);
    DidomiUrlInput = /*@__PURE__*/ createReactComponent(`${prefix ? `${prefix}-` : ''}didomi-url-input`);
};

const ModalPortal = (props) => {
    const [container] = useState(() => {
        let tmpContainer = document.querySelector(props.attachTo) || document.querySelector('.didomi-overlay-container');
        if (!tmpContainer) {
            tmpContainer = document.createElement('div');
            tmpContainer.classList.add('didomi-overlay-container');
            document.body.appendChild(tmpContainer);
        }
        return tmpContainer;
    });
    return createPortal(props.children, container);
};
const DidomiModalPortalized = (props) => {
    return (React.createElement(ModalPortal, null,
        React.createElement(DidomiModal, Object.assign({}, props), props.children)));
};

/**
 * Gets the value from an object in a string like path. The path could include nested level via dot separation
 * Eg resolvePath(object, 'first.second') will return object[first][second]
 *
 * @param object the object to extract the value from
 * @param path the path to get the value inside the object
 * @param defaultValue an optional default value to return if the value is not found
 * @returns the value inside the object following the expected path
 */
function resolvePath(object, path, defaultValue) {
    return path.split('.').reduce((o, p) => (o ? o[p] : defaultValue), object);
}
/**
 * If the form field is invalid and touched, returns the error, otherwise returns `undefined`.
 *
 * @param name the name of the field
 * @param name the Formik form state
 */
function getError(name, form) {
    const error = resolvePath(form.errors, name);
    const touched = resolvePath(form.touched, name);
    if (error && touched) {
        return error;
    }
    return undefined;
}
/**
 * Handles the `focusout` event for the Field component.
 * We use `focusout` over `blur` because `focusout` supports bubbling.
 * This means that the `blur` event won't be triggered because we aren't listening directly to the native input,
 * but `focusout` on the other hand will bubble from the native input to the parents.
 */
function handleFocusout(field, form) {
    form.setFieldValue(field.name, field.value, true);
    form.setFieldTouched(field.name, true, false);
}
/** Handle the `valueChange` event for the Field component */
function handleValueChange(field, form, event) {
    form.setFieldValue(field.name, event.detail, form.touched.hasOwnProperty(field.name));
}

const DidomiTextInputField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiTextInput, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props), props.children))));
};

const DidomiNumberInputField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiNumberInput, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props), props.children))));
};

const DidomiPasswordInputField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiPasswordInput, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiDatepickerField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiDatepicker, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiCheckboxField = props => {
    return (React.createElement(Field, { name: props.name, type: "checkbox" }, ({ field, form }) => (React.createElement(DidomiCheckbox, Object.assign({ className: props.className, checked: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiSelectField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiSelect, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props), props.children))));
};

const DidomiTextareaField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiTextarea, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiRadioGroupField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiRadioGroup, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props), props.children))));
};

const DidomiAutocompleteField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiAutocomplete, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props), props.children))));
};

const DidomiAutocompleteWithSearchField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiAutocompleteWithSearch, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiSwitchField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiSwitch, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props), props.children))));
};

const DidomiColorPickerField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiColorPicker, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiColorItemField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiColorItem, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiColorPickerInputField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiColorPickerInput, Object.assign({ className: props.className, value: field.value, 
        // error={getError(field.name, form)} TODO implement this when the component has error state
        onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiEditableTextBoxField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiEditableTextBox, Object.assign({ className: props.className, value: field.value, onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event) }, props)))));
};

const DidomiToggleButtonField = props => {
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => (React.createElement(DidomiToggleButton, Object.assign({ className: props.className, value: field.value, onValueChange: (event) => handleValueChange(field, form, event) }, props), props.children))));
};

const DidomiUrlInputField = props => {
    const { checkStatus } = props;
    return (React.createElement(Field, { name: props.name, type: "text" }, ({ field, form }) => {
        return (React.createElement(DidomiUrlInput, Object.assign({ className: props.className, value: field.value, error: getError(field.name, form), onFocusout: () => handleFocusout(field, form), onValueChange: (event) => handleValueChange(field, form, event), checkStatus: checkStatus }, props)));
    }));
};

const setUpDidomiUIAtoms = async (prefix) => {
    setupComponents(prefix);
    await defineCustomElements(window, { transformTagName: (tagName) => `${prefix ? `${prefix}-` : ''}${tagName}` });
};

export { DidomiAutocomplete, DidomiAutocompleteField, DidomiAutocompleteInput, DidomiAutocompleteWithSearch, DidomiAutocompleteWithSearchField, DidomiBackButtonLink, DidomiBackToTopButton, DidomiBaseInput, DidomiBottomBar, DidomiButton, DidomiButtonGroup, DidomiCard, DidomiCardContainer, DidomiCardRadio, DidomiCarousel, DidomiCarouselContentSlide, DidomiCarouselSlide, DidomiCheckbox, DidomiCheckboxField, DidomiChip, DidomiChipList, DidomiCollapse, DidomiCollapsibleCard, DidomiCollapsibleSection, DidomiCollapsibleTabHeader, DidomiCollapsibleTabHeaders, DidomiCollapsibleTabHeading, DidomiCollapsibleTabPanel, DidomiCollapsibleTabPanels, DidomiCollapsibleTabs, DidomiColorItem, DidomiColorItemField, DidomiColorPicker, DidomiColorPickerField, DidomiColorPickerInput, DidomiColorPickerInputField, DidomiContentAnalyticsCard, DidomiContentPromotionCard, DidomiContentPromotionCardShortenedItems, DidomiControlFlowHeader, DidomiCopyText, DidomiCtaPopIn, DidomiDatepicker, DidomiDatepickerField, DidomiDrawer, DidomiDrawerContent, DidomiDrawerHeader, DidomiDropdown, DidomiEditableTextBox, DidomiEditableTextBoxField, DidomiEmptyState, DidomiErrorState, DidomiFabButton, DidomiFeatureHeader, DidomiFiltersBar, DidomiFiltersContainer, DidomiFloatingPasswordMeter, DidomiHeaderIllustration, DidomiHeaderInput, DidomiHintbox, DidomiHubspotForm, DidomiIcon, DidomiIconButton, DidomiIllustration, DidomiInstructionStep, DidomiLanguageFlag, DidomiLargePush, DidomiLink, DidomiListShortener, DidomiLoader, DidomiMenu, DidomiMenuItem, DidomiMenuSeparator, DidomiModalPortalized as DidomiModal, DidomiModalActions, DidomiModalContent, DidomiModalHeader, DidomiNavTabContent, DidomiNavTabContents, DidomiNavTabHeader, DidomiNavTabHeaders, DidomiNavTabHeading, DidomiNavTabs, DidomiNumberInput, DidomiNumberInputField, DidomiOption, DidomiPaginator, DidomiPartnerButton, DidomiPasswordInput, DidomiPasswordInputField, DidomiPasswordMeter, DidomiProgressBar, DidomiPromotionalPush, DidomiRadio, DidomiRadioGroup, DidomiRadioGroupField, DidomiRightPanel, DidomiRightPanelTab, DidomiSearchBar, DidomiSecondaryHeader, DidomiSelect, DidomiSelectCounter, DidomiSelectField, DidomiSelectOption, DidomiSelectOptions, DidomiSelectPlainInput, DidomiSelectionBlock, DidomiSelectionCardItem, DidomiSkeleton, DidomiSmallPush, DidomiSmallPushCompact, DidomiSnackbar, DidomiStepper, DidomiStepperStep, DidomiSwitch, DidomiSwitchField, DidomiTabHeader, DidomiTabHeaders, DidomiTabHeading, DidomiTabPanel, DidomiTabPanels, DidomiTable, DidomiTableBody, DidomiTableCard, DidomiTableCardItem, DidomiTableHeaderRow, DidomiTableHeading, DidomiTableRow, DidomiTableTd, DidomiTableTh, DidomiTabs, DidomiTextInput, DidomiTextInputField, DidomiTextSearchInput, DidomiTextarea, DidomiTextareaField, DidomiThemingCardOption, DidomiTimeline, DidomiTimelineItem, DidomiToggleButton, DidomiToggleButtonField, DidomiToggleButtonOption, DidomiTooltip, DidomiTreeCard, DidomiTreeCardBranch, DidomiTreeCardBranchItem, DidomiUrlInput, DidomiUrlInputField, setUpDidomiUIAtoms, setupComponents };
//# sourceMappingURL=index.esm.js.map
